import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome, 
  faSignInAlt, 
  faUserPlus, 
  faTachometerAlt, 
  faInbox, 
  faList, 
  faQuestionCircle, 
  faSignOutAlt, 
  faPlusCircle,
  faBars,
  faTimes,
  faSearch,
  faInfoCircle,
  faNewspaper,
  faUserTie,
  faCalculator
} from '@fortawesome/free-solid-svg-icons';
import Logo from './bodex_crop.png';
import axios from '../../axios';
import config from '../../config';
import { trackFormSubmission } from '../../utils/analytics';
import { useLanguage } from '../../i18n/LanguageContext';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';

import './Header.css';

const Header = ({ handleLogout, loggedIn, userType, setShowLoginOverlay, refreshTrigger = 0 }) => {
  const history = useHistory();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const backend_url = config.backend_url;
  const { t } = useLanguage();

  useEffect(() => {
    // Only fetch unread count when user is logged in and not currently loading
    if (loggedIn && !isLoading) {
      fetchUnreadCount();
    }
  }, [loggedIn, refreshTrigger]); // Only depend on loggedIn status and explicit refresh trigger

  const fetchUnreadCount = async () => {
    // Prevent multiple simultaneous requests
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const response = await axios.get(`${backend_url}/api/conversations/unread-count`, { 
        withCredentials: true 
      });
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginClick = () => {
    // Use the login overlay instead of redirecting to login page
    setShowLoginOverlay(true);
    closeMenu();
  };

  const handleCreateAdClick = () => {
    trackFormSubmission('ClickToObjectForm_Header', {
      event_category: 'Navigation',
      event_label: 'Clicked Skapa annons from Header'
    });
    history.push('/object-form?workflow=slim');
    closeMenu();
  };

  const handleValuationClick = () => {
    history.push('/valuation');
    closeMenu();
  };

  const openMenu = () => {
    document.body.classList.add('open-aside');
  };

  const closeMenu = () => {
    document.body.classList.remove('open-aside');
  };

  const navLinkClass = ({ isActive }) => (isActive ? 'nav-link active' : 'nav-link');

  return (
    <>
      {/* Colorful horizontal line */}
      <div className="colorful-line"></div>
      {/* <LanguageSwitcher /> */}

      <header className="header-wrapper auth-header">
        <div className="container">
          <div className="header-content">
            {/* Logo / Home link */}
            <div className="logo-container">
              <span className="link" onClick={() => { history.push('/'); closeMenu(); }}>
                <img src={Logo} alt={t('header.logoAlt')} className="logo" />
              </span>
            </div>

            {/* Main navigation + Mobile menu */}
            <div className="nav-container">
              <div className="navigation">
                {/* Mobile close icon */}
                <div className="mbl-cross" onClick={closeMenu}>
                  <FontAwesomeIcon icon={faTimes} />
                </div>

                <ul>
                  <li onClick={() => { history.push('/'); closeMenu(); }}>
                    <FontAwesomeIcon icon={faHome} />
                    <span>{t('header.home')}</span>
                  </li>

                  {/* If not logged in, show these */}
                  {!loggedIn && (
                    <>
                      <li onClick={handleCreateAdClick}>
                        <FontAwesomeIcon icon={faPlusCircle} />
                        <span>{t('header.createAd')}</span>
                      </li>
                    </>
                  )}

                  {/* If logged in, show user type specific options */}
                  {loggedIn && userType === 'seller' && (
                    <>
                      <li onClick={() => { history.push('/broker-search'); closeMenu(); }}>
                        <FontAwesomeIcon icon={faSearch} />
                        <span>{t('header.findBroker')}</span>
                      </li>
                      <li onClick={() => { history.push('/inbox'); closeMenu(); }}>
                        <FontAwesomeIcon icon={faInbox} />
                        <span>{t('header.inbox')}</span>
                        {unreadCount > 0 && <div className="notification-badge">{unreadCount}</div>}
                      </li>
                    </>
                  )}

                  {loggedIn && userType === 'broker' && (
                    <>
                      <li onClick={() => { history.push('/object-dashboard'); closeMenu(); }}>
                        <FontAwesomeIcon icon={faList} />
                        <span>{t('header.allObjects')}</span>
                      </li>
                      <li onClick={() => { history.push('/inbox'); closeMenu(); }}>
                        <FontAwesomeIcon icon={faInbox} />
                        <span>{t('header.inbox')}</span>
                        {unreadCount > 0 && <div className="notification-badge">{unreadCount}</div>}
                      </li>
                    </>
                  )}

                  <li onClick={handleValuationClick}>
                    <FontAwesomeIcon icon={faCalculator} />
                    <span>{t('header.valueHome')}</span>
                  </li>
                  <li onClick={() => { history.push('/faq'); closeMenu(); }}>
                    <FontAwesomeIcon icon={faQuestionCircle} />
                    <span>{t('header.faq')}</span>
                  </li>
                  
{/*                   <li onClick={() => { history.push('/blog'); closeMenu(); }}>
                    <FontAwesomeIcon icon={faNewspaper} />
                    <span>Blogg</span>
                  </li> */}
                  {!loggedIn && (
                    <li onClick={() => { history.push('/brokerstart'); closeMenu(); }}>
                      <FontAwesomeIcon icon={faUserTie} />
                      <span>{t('header.broker')}</span>
                    </li>
                  )}
                </ul>
              </div>

              {/* Mobile hamburger menu icon */}
              <div className="mbl-menu" onClick={openMenu}>
                <FontAwesomeIcon icon={faBars} />
              </div>

              {/* Mobile valuation button */}
              <button className="btn btn-primary valuation-btn mobile-only" onClick={handleValuationClick}>
                <FontAwesomeIcon icon={faCalculator} />
                <span>{t('common.value')}</span>
              </button>

              {/* Action button (Login/Logout) */}
              {!loggedIn ? (
                <button className="btn btn-primary create-ad-btn" onClick={handleLoginClick}>
                  <FontAwesomeIcon icon={faSignInAlt} />
                  <span>{t('header.login')}</span>
                </button>
              ) : (
                <button className="btn btn-primary logout-btn" onClick={() => { handleLogout(); closeMenu(); }}>
                  <FontAwesomeIcon icon={faSignOutAlt} />
                  <span>{t('common.logout')}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
